import { Box, Button, Collapse, Link, styled, Typography } from '@mui/material';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import { useFormikContext } from 'formik';
import { IRenewalMakePaymentForm } from '@acorn-portal/shared/src/pages/renewal/MakePayment';
import { FC, useEffect, useState } from 'react';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import { FieldSet } from '@acorn-portal/shared/src/components/fieldSet';
import { SavedPaymentMethodDetails } from '@acorn-portal/shared/src/services/renewalsApi';
import FormFieldCheckBox from '@acorn-portal/shared/src/components/inputs/FormFieldCheckbox';
import FinancialQuestions from '../financial-questions';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { PaymentMethodResponse } from '@acorn-portal/shared/src/services/managedPaymentsApi';
import extractBeforeDash from '../../../utils/extractBeforeDash';
import getDay from '@acorn-portal/shared/src/utils/get-day';
import { getFinanceWording } from '@acorn-portal/shared/src/utils/get-finance-wording';

interface IDirectDebitDetils {
	directDebitDetails?: SavedPaymentMethodDetails;
	newDirectDebitPaymentDay?: number;
	managedPaymentDetails?: PaymentMethodResponse;
}

export const AlignedText = styled(Typography)(({ theme }) => ({
	marginBottom: theme.spacing(2),
	textAlign: 'left',
	marginTop: theme.spacing(2),
	[theme.breakpoints.up('md')]: {
		marginLeft: theme.spacing(40),
		paddingRight: '20%',
	},
}));

const termsModal = {
	title: 'Direct Debit Terms and Conditions',
	description: `<p>The Guarantee is offered by all banks and building societies that accept instructions to pay Direct Debits.
  <p/>
 <p> If there are any changes to the amount, date or frequency of your Direct Debit Acorn Insurance & Financial services will notify you (normally 10 working days) in advance of your account being debited or as otherwise agreed. If you request the Acorn Insurance & Financial services to collect a payment, confirmation of the amount and date will be given to you at the time of the request.
  <p/>
  <p>If an error is made in the payment of your Direct Debit, by Acorn Insurance & Financial services or your bank or building society, you are entitled to a full and immediate refund of the amount paid from your bank or building society. If you receive a refund you are not entitled to, you must pay it back when Acorn Insurance & Financial services asks you to.
  <p/>
 <p> You can cancel a Direct Debit at any time by simply contacting your bank or building society. Written confirmation may be required. Please also notify the organisation.</p>`,
};

const CarrotTermsModal = {
	title: 'Terms and Conditions',
	description: `<p>The Guarantee is offered by all banks and building societies that accept instructions to pay Direct Debits. If there are any changes to the amount, date or frequency of your Direct Debit Carrot Insurance will notify you (normally 10 working days) in advance of your account being debited or as otherwise agreed. If you request Carrot Insurance to collect a payment, confirmation of the amount and date will be given to you at the time of the request.
<p/>
<p> If an error is made in the payment of your Direct Debit, by Carrot Insurance or your bank or building society, you are entitled to a full and immediate refund of the amount paid from your bank or building society. If you receive a refund you are not entitled to, you must pay it back when Carrot Insurance asks you to.
<p/>

<p> You can cancel a Direct Debit at any time by simply contacting your bank or building society. Written confirmation may be required. Please also notify the organisation.</p>`,
};

const MyPolicyTermsModal = {
	title: 'Direct Debit Terms and Conditions',
	description: `<p>The Guarantee is offered by all banks and building societies that accept instructions to pay Direct Debits
  <p/>
 <p> If there are any changes to the amount, date or frequency of your Direct Debit My Policy will notify you (normally 10 working days) in advance of your account being debited or as otherwise agreed. If you request the My Policy services to collect a payment, confirmation of the amount and date will be given to you at the time of the request
  <p/>
  <p>If an error is made in the payment of your Direct Debit, by My Policy or your bank or building society, you are entitled to a full and immediate refund of the amount paid from your bank or building society. If you receive a refund you are not entitled to, you must pay it back when My Policy asks you to
  <p/>
 <p> You can cancel a Direct Debit at any time by simply contacting your bank or building society. Written confirmation may be required. Please also notify the organisation.</p>`,
};

const DirectDebitDetails: FC<IDirectDebitDetils> = ({ directDebitDetails, newDirectDebitPaymentDay }) => {
	const [open, toggleModal] = useState(false);
	const formik = useFormikContext<IRenewalMakePaymentForm>();
	const fullPolicy = useAppSelector((x) => x.fullPolicy);
	const isCarrot = fullPolicy.product === Product.CARROT;
	const isMyPolicy = fullPolicy.product === Product.TELEMATICS && import.meta.env.VITE_AUTH_REALM === 'MyPolicy';
	const company = extractBeforeDash(import.meta.env.VITE_JOURNEY ?? '');
	const instalmentWording = getFinanceWording(company);

	const toggleEdit = () => {
		formik.setFieldValue('new_direct_debit', !formik.values.new_direct_debit);
		formik.setErrors({});
	};

	const hasDirectDebitDetails = directDebitDetails?.bankOrBuildingSociety;

	useEffect(() => {
		if (!hasDirectDebitDetails) {
			formik.setFieldValue('new_direct_debit', true);
		}
	}, []);

	const canEnterNewDetails =
		formik.values.is_account_holder === 'yes' && formik.values.is_only_signatory_required === 'yes';

	const formattedNewPaymentDay = newDirectDebitPaymentDay ? getDay(newDirectDebitPaymentDay) : '';

	const getModalType = () => {
		if (isCarrot) {
			return CarrotTermsModal;
		}
		if (isMyPolicy) {
			return MyPolicyTermsModal;
		}
		return termsModal;
	};

	const modal = getModalType();

	return (
		<FieldSet title='Direct Debit Details'>
			<Collapse in={!!hasDirectDebitDetails && !formik.values.new_direct_debit}>
				<Box
					sx={(theme) => ({
						border: `1px solid ${theme.palette.primary.main}`,
						p: 3,
						borderRadius: '8px',
						width: { xs: '100%' },
						textAlign: 'left',
						maxWidth: '400px',
						ml: 'auto',
						mr: 'auto',
					})}
				>
					<Typography variant='h6' color='primary'>
						Current Details
					</Typography>
					<Typography>Bank Name: {directDebitDetails?.bankOrBuildingSociety}</Typography>
					<Typography>Account Number: {directDebitDetails?.maskedAccountNumber}</Typography>
					<Button onClick={toggleEdit} sx={{ mt: 5 }} variant='link'>
						Change Payment Details
					</Button>
				</Box>
			</Collapse>
			<Collapse in={!hasDirectDebitDetails || formik.values.new_direct_debit}>
				<Box sx={{ textAlign: { xs: 'left', md: 'unset' } }}>
					<FinancialQuestions formik={formik} />

					<Collapse in={canEnterNewDetails}>
						<FormTextField
							name='account_number'
							label='Account Number'
							value={formik.values.account_number}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.account_number && Boolean(formik.errors.account_number)}
							helperText={formik.touched.account_number && formik.errors.account_number}
							format='accountNumber'
						/>
						<FormTextField
							id='sort_code_field'
							name='sort_code'
							label='Sort Code'
							value={formik.values.sort_code}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.sort_code && Boolean(formik.errors.sort_code)}
							helperText={formik.touched.sort_code && formik.errors.sort_code}
							format='sortCode'
						/>
						{hasDirectDebitDetails &&
							formattedNewPaymentDay &&
							formik.values.account_number &&
							formik.values.sort_code && (
								<AlignedText sx={{ marginBottom: 5 }}>
									As your Direct Debit details have changed, we will take your payment on the {formattedNewPaymentDay}{' '}
									of every month or the next working day if the {formattedNewPaymentDay} falls on a weekend. Please see
									your payment schedule for further details.
								</AlignedText>
							)}
						<AlignedText sx={{ marginBottom: 5 }}>{instalmentWording}</AlignedText>
					</Collapse>

					{hasDirectDebitDetails && (
						<Button onClick={toggleEdit} variant='link'>
							Keep Same Details
						</Button>
					)}
				</Box>
			</Collapse>

			<Box sx={{ mt: 2 }}>
				<FormFieldCheckBox
					label=''
					onChange={() => {
						formik.setFieldValue('accept_dd_terms', !formik.values.accept_dd_terms);
					}}
					onBlur={() => formik.setFieldTouched('accept_dd_terms')}
					options={[
						{
							label: (
								<Typography
									color={formik.touched.accept_dd_terms && Boolean(formik.errors.accept_dd_terms) ? 'error' : undefined}
								>
									Please confirm the above details are correct, and that you agree with the{' '}
									<Link onClick={() => toggleModal(true)}>Terms and Conditions</Link>
								</Typography>
							),
							value: formik.values.accept_dd_terms,
						},
					]}
					error={formik.touched.accept_dd_terms && Boolean(formik.errors.accept_dd_terms)}
					helperText={formik.touched.accept_dd_terms && formik.errors.accept_dd_terms}
				/>

				{termsModal && <HelperModal onClose={() => toggleModal(false)} open={open} {...modal} />}
			</Box>
		</FieldSet>
	);
};

export default DirectDebitDetails;
